<app-page-header [title]="'Sample Details'" [subtitle]="'View details of the selected sample'"></app-page-header>

<h2 class="text-2xl font-semibold mb-2">Sample Information</h2>
<div class="mb-6 border border-gray-300 dark:border-gray-700 p-4 rounded-md">
    <div class="grid grid-cols-2 gap-2">
        <!-- Left Column -->
        <div class="flex flex-col gap-1">
            <p><strong>Printed Name:</strong> {{ sample?.printedName }}</p>
            <p><strong>ID:</strong> {{ sample?.id }}</p>
            <p><strong>ES2 Key:</strong> {{ sample?.es2Key }}</p>
            <p><strong>SSP Name:</strong> {{ sample?.sspName }}</p>
            <p><strong>Weight:</strong> {{ sample?.weight + 'g' }}</p>
            <p><strong>Format:</strong> {{ sample?.format | sampleFormat }}</p>
            <p><strong>Default Sample Branding:</strong> {{ sample?.defaultSampleBranding | sampleBranding }}</p>
            <p><strong>Created By:</strong> {{ sample?.createdBy | roleName }}</p>
            <p><strong>Updated By:</strong> {{ sample?.updatedBy | roleName }}</p>
            <p>
                <strong>Last Activity:</strong>
                {{ sample?.lastActivity | formatDateTime }}
                ({{ sample?.lastActivityBy | roleName }})
            </p>
            @if (sample?.media) {
                <div>
                    <p><strong>Image:</strong></p>
                    <p-image
                            [src]="sample?.media?.content?.cloudfrontUrl || 'assets/images/no-image.png'"
                            alt="Sample Image"
                            preview="true"
                            width="250"
                    ></p-image>
                </div>
            }
        </div>

        <!-- Right Column -->
        <div class="flex flex-col gap-1">
            <p><strong>Field of Use:</strong></p>
            <p-tag
                    [value]="sample?.fieldOfUse | enumLabel"
                    [severity]="sample?.fieldOfUse | fieldOfUseSeverity"
            ></p-tag>

            <p><strong>Type:</strong></p>
            <p-tag
                    [value]="sample?.type?.name"
                    severity="secondary"
            ></p-tag>

            <p><strong>Category:</strong></p>
            <p-tag
                    [value]="sample?.category?.name ?? 'No category'"
                    severity="secondary"
            ></p-tag>

            <p><strong>State:</strong></p>
            <p-tag
                    [value]="sample?.onHold ? (sample?.state | enumLabel) + ' (On Hold)' : (sample?.state | enumLabel)"
                    [severity]="sample?.onHold ? 'warn' : (sample?.state | sampleStateSeverity)"
            ></p-tag>

            @if (sample?.categoryDetails?.length) {
                <p><strong>Details:</strong></p>
                <ul class="list-disc list-inside ml-2">
                    @for (detail of sample?.categoryDetails || []; track detail) {
                        <li>
                            {{ detail.name }} @if (detail.isNamingRelevant) {
                            <span class="text-sm text-gray-500">(Naming Relevant)</span>
                        }
                        </li>
                    }
                </ul>
            }
        </div>
    </div>
</div>

<!-- Sales Terms -->
@if (sample?.sampleLanguages?.length) {
    <div class="mb-6">
    <h2 class="text-2xl font-semibold mb-2">Sales Terms</h2>
    <div class="border border-gray-300 dark:border-gray-700 p-4 rounded-md">
        <p-table [value]="sample?.sampleLanguages ?? []" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
                <tr>
                    <th>Language</th>
                    <th>Sales Term</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-language>
                <tr>
                    <td>{{ getLanguageName(language.languageId) }}</td>
                    <td>{{ language.salesTerm }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    </div>
}

<!-- Manufacturer Details -->
@if (sample?.manufacturer) {
    <div class="mb-6">
    <h2 class="text-2xl font-semibold mb-2">Manufacturer</h2>
    <div class="border border-gray-300 dark:border-gray-700 p-4 rounded-md">
        <div class="flex justify-between">
            <div>
                <p><strong>Name:</strong> {{ sample?.manufacturer?.name }}</p>
                <p><strong>Email:</strong> {{ sample?.manufacturer?.mail }}</p>
                <p><strong>Address:</strong> {{ sample?.manufacturer?.address?.street }}</p>
                <p><strong>City:</strong> {{ sample?.manufacturer?.address?.city }}</p>
                <p><strong>Country:</strong> {{ sample?.manufacturer?.address?.country }}</p>
            </div>
            <p-button label="View Manufacturer" icon="pi pi-eye"
                      routerLink="/manufacturers/manufacturer/{{sample?.manufacturer?.id}}"
                      outlined
            ></p-button>
        </div>
    </div>
    </div>
}
<!-- Sample Vehicle Models (hidden for producers) -->
@if (canSeeVehicleModels()) {
<div class="mb-6">
    <h2 class="text-2xl font-semibold mb-2">Vehicle Models</h2>
    <div class="border border-gray-300 dark:border-gray-700 p-4 rounded-md">
        @if (vehicleModels.length) {
            <div class="mb-4">
                <p-table #vmTable
                         [globalFilterFields]="['label', 'modelSeries', 'brand.label', 'bodyType.label', 'carClass.label', 'salesCodes']"
                         [paginator]="true"
                         [rows]="5"
                         [value]="vehicleModels"
                         sortMode="multiple">
                    <ng-template pTemplate="caption">
                        <div class="flex gap-2">
                            <p-iconfield iconPosition="left">
                                <p-inputicon>
                                    <i class="pi pi-search"></i>
                                </p-inputicon>
                                <input (input)="onVehicleModelSearch($event, vmTable)"
                                       pInputText
                                       placeholder="Search"
                                       type="text"/>
                            </p-iconfield>
                        </div>
                    </ng-template>
                    <ng-template pTemplate="header">
                        <tr>
                            <th pSortableColumn="label">Label
                                <p-sortIcon field="label"></p-sortIcon>
                            </th>
                            <th pSortableColumn="modelSeries">Model Series
                                <p-sortIcon field="modelSeries"></p-sortIcon>
                            </th>
                            <th pSortableColumn="brand.label">Brand
                                <p-sortIcon field="brand.label"></p-sortIcon>
                            </th>
                            <th pSortableColumn="bodyType.label">Body Type
                                <p-sortIcon field="bodyType.label"></p-sortIcon>
                            </th>
                            <th pSortableColumn="carClass.label">Car Class
                                <p-sortIcon field="carClass.label"></p-sortIcon>
                            </th>
                            <th pSortableColumn="sampleAvailabilityDate">Available
                                <p-sortIcon field="sampleAvailabilityDate"></p-sortIcon>
                            </th>
                            <th pSortableColumn="sampleExpirationDate">Expires
                                <p-sortIcon field="sampleExpirationDate"></p-sortIcon>
                            </th>
                            <th>Branding</th>
                            <th pSortableColumn="salesCodes">Sales Codes
                                <p-sortIcon field="salesCodes"></p-sortIcon>
                            </th>
                            <th></th>
                        </tr>
                    </ng-template>
                    <ng-template let-vehicleModel pTemplate="body">
                        <tr>
                            <td>{{ vehicleModel.label }}</td>
                            <td>{{ vehicleModel.modelSeries }}</td>
                            <td>{{ vehicleModel.brand?.label || '' }}</td>
                            <td>{{ vehicleModel.bodyType?.label || '' }}</td>
                            <td>{{ vehicleModel.carClass?.label || '' }}</td>
                            <td>{{ vehicleModel.sampleAvailabilityDate | formatDate }}</td>
                            <td>{{ vehicleModel.sampleExpirationDate | formatDate }}</td>
                            <td>
                                <p-select
                                        isMaintainer
                                        (onChange)="onChangeBranding($event.value, vehicleModel)"
                                        [(ngModel)]="vehicleModel.branding"
                                        [options]="brandingOptions"
                                        appendTo="body"
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select branding"
                                        styleClass="w-full"
                                ></p-select>
                            </td>
                            <td class="whitespace-normal break-words max-w-[200px]">
                                {{ vehicleModel.salesCodes }}
                            </td>
                            <td>
                                <div class="flex justify-end gap-2">
                                    <p-button icon="pi pi-eye" pTooltip="View vehicle model"
                                              routerLink="/vehicle-models/vehicle-model/{{vehicleModel.id}}"
                                              text
                                              tooltipPosition="left"></p-button>
                                    <p-button (click)="openChangeSalesCodesDialog(vehicleModel)" icon="pi pi-barcode"
                                              isMaintainer
                                              pTooltip="Change sales codes"
                                              text
                                              tooltipPosition="left"></p-button>
                                    <p-button (click)="openChangeVehicleModelAvailabilityDialog(vehicleModel)"
                                              icon="pi pi-clock"
                                              isMaintainer
                                              pTooltip="Change availability"
                                              text
                                              tooltipPosition="left"></p-button>
                                    <p-button (click)="removeVehicleModel(vehicleModel)" icon="pi pi-minus"
                                              isMaintainer
                                              pTooltip="Remove vehicle model"
                                              severity="danger"
                                              text
                                              tooltipPosition="left"></p-button>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        }

        <p-button isMaintainer label="Assign Vehicle Models" icon="pi pi-plus"
                  (click)="openAddVehicleModelsDialog()"></p-button>
    </div>
</div>
}

<!-- Sample Market Availability -->
@if (this.marketAvailabilities.length) {
    <div class="mb-6">
    <h2 class="text-2xl font-semibold mb-2">Market Availability</h2>
    <div class="border border-gray-300 dark:border-gray-700 p-4 rounded-md">
        <p-table [value]="marketAvailabilities" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
                <tr>
                    <th>Country</th>
                    <th>DEP Market</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-marketAvailability>
                <tr>
                    <td>{{ marketAvailability.country.name }}</td>
                    <td>{{ marketAvailability.country.depMarketName }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    </div>
}

<!-- Sample Process Logs -->
@if (processLogs.length) {
    <div class="mb-6">
    <h2 class="text-2xl font-semibold mb-2">Process Logs</h2>
    <div class="border border-gray-300 dark:border-gray-700 p-4 rounded-md">
        <p-table [value]="processLogs" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
                <tr>
                    <th>Comment</th>
                    <th>From State</th>
                    <th>To State</th>
                    <th>Timestamp</th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-processLog>
                <tr>
                    <td>{{ processLog.comment || '' }}</td>
                    <td>
                        <p-tag
                                [value]="processLog.fromState | enumLabel"
                                [severity]="processLog.fromState | sampleStateSeverity"
                        ></p-tag>
                    </td>
                    <td>
                        <p-tag
                                [value]="processLog.toState | enumLabel"
                                [severity]="processLog.toState | sampleStateSeverity"
                        ></p-tag>
                    </td>
                    <td>{{ processLog.timestamp | formatDateTime }}</td>
                    <td>
                        <div class="flex justify-end">
                            <p-button
                                    icon="pi pi-envelope"
                                    text
                                    pTooltip="Resend mail"
                                    tooltipPosition="left"
                                    [disabled]="processLog.comment?.startsWith('Resend mail')"
                                    (click)="resendMail(processLog.id)">
                            </p-button>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    </div>
}
<!-- Action Buttons -->
<div class="flex justify-between items-center mt-6">
    <div class="flex gap-3">
        <p-button isMaintainer [hidden]="false" label="Edit" icon="pi pi-pencil" (click)="openEditDialog()"
                  severity="secondary" text></p-button>
        <p-button isProducer label="Request Master Sample" icon="pi pi-database" (click)="requestMasterSample()"
                  [hidden]="!showRequestMasterSampleButton() || !!sample?.onHold" severity="secondary" text></p-button>
        <p-button label="Resend Latest Mail" icon="pi pi-envelope" (click)="resendLatestMail()"
                  [hidden]="(this.sample?.state === SampleState.CREATED) || sample?.onHold"
                  severity="secondary" text></p-button>
        <p-button label="On Hold" icon="pi pi-stop-circle" (click)="toggleOnHold()"
                  canToggleOnHold
                  [hidden]="this.sample?.state === SampleState.ACTIVE || this.sample?.state === SampleState.ARCHIVED"
                  severity="secondary" text></p-button>
        <p-button
                isMaintainer
                label="Publish"
                icon="pi pi-arrow-right"
                (click)="publishSample()"
                [hidden]="!showPublishSampleButton()  || !!sample?.onHold"
                severity="secondary"
                text
        ></p-button>
        <p-button
                isProducer
                label="Request Approval"
                icon="pi pi-question-circle"
                (click)="requestApproval()"
                [hidden]="!showRequestApprovalButton()  || !!sample?.onHold"
                severity="secondary"
                text
        ></p-button>
        <p-button
                isProducer
                label="Approve"
                icon="pi pi-check"
                (click)="approveSampleByReviewer()"
                [hidden]="!showApproveSampleByReviewerButton()  || !!sample?.onHold"
                severity="secondary"
                text
        ></p-button>
        <p-button
                isProducer
                label="Request Changes"
                icon="pi pi-times"
                (click)="requestChangesByReviewer()"
                [hidden]="!showRequestChangesButton()  || !!sample?.onHold"
                severity="secondary"
                text
        ></p-button>
        <p-button
                isProcessOwner
                label="Set In Production"
                icon="pi pi-cog"
                (click)="setInProduction()"
                [hidden]="!showSetInProductionButton()  || !!sample?.onHold"
                severity="secondary"
                text
        ></p-button>
        <p-button
                isProcessOwner
                label="Set active"
                icon="pi pi-verified"
                (click)="approveSampleByProcessOwner()"
                [hidden]="!showApproveSampleByProcessOwnerButton()  || !!sample?.onHold"
                severity="secondary"
                text
        ></p-button>
        <p-button
                (click)="approveSampleByProcessOwner()"
                [hidden]="!showSetActiveByProducerButton() || isProcessOwner() || !!sample?.onHold"
                icon="pi pi-verified"
                isProducer
                label="Set active"
                severity="secondary"
                text
        ></p-button>
        <p-button isMaintainer [hidden]="false" label="Delete" icon="pi pi-trash" (click)="deleteSample()"
                  severity="danger" text
                  [disabled]="sample?.state === SampleState.ACTIVE || sample?.state === SampleState.ARCHIVED"
        ></p-button>
    </div>
    <p-button label="Back to Samples" icon="pi pi-arrow-left" text routerLink="/samples"></p-button>
</div>

<!-- Update Dialog -->
<app-update-sample-dialog
        [(visible)]="displayUpdateDialog"
        [sample]="editableSample"
        (sampleUpdated)="reloadSampleDetails()"
></app-update-sample-dialog>

<!-- Change State Dialog -->
<app-change-sample-state-dialog
        [(visible)]="displayChangeStateDialog"
        [header]="changeStateDialogHeader"
        [message]="changeStateDialogMessage"
        [sampleId]="this.sample?.id || 0"
        [newSampleState]="changeStateDialogNewSampleState"
        (sampleStateChange)="reloadSampleDetails()">
</app-change-sample-state-dialog>

<app-add-vehicle-models-dialog
        [(visible)]="displayAddVehicleModelsDialog"
        [sampleId]="this.sample?.id || 0"
        (vehicleModelsAdded)="this.reloadSampleDetails()"
></app-add-vehicle-models-dialog>

<app-change-sample-vehicle-model-availability-dialog
        [(visible)]="displayChangeVehicleModelAvailabilityDialog"
        [sample]="this.sample"
        [vehicleModel]="this.selectedVehicleModel"
        (availabilityChange)="reloadSampleDetails()"
></app-change-sample-vehicle-model-availability-dialog>

<app-change-sales-codes-dialog
        [(visible)]="displayChangeSalesCodesDialog"
        [sample]="this.sample"
        [vehicleModel]="this.selectedVehicleModel"
        (salesCodesChange)="reloadSampleDetails()"
></app-change-sales-codes-dialog>

<app-toggle-on-hold-dialog
        [(visible)]="showOnHoldDialog"
        [sample]="this.sample"
        (toggled)="reloadSampleDetails()"
/>
